import Phaser from "phaser";
import type { EventType } from "../domain/types";

const COLORS = {
  threat: { accent: "#c62828", icon: "!" },
  opportunity: { accent: "#0076a8", icon: "★" },
  recovery: { accent: "#d0087f", icon: "♥" },
} as const;

const TEXT_STYLE = {
  fontFamily: "Arial, system-ui, sans-serif",
  fontStyle: "bold",
} as const;

function responsiveFontSize(sceneWidth: number, word: string): number {
  const maximum = sceneWidth < 600 ? 22 : 24;
  const availableWidth = Math.min(sceneWidth * 0.72, 410);
  const fitted = Math.floor(availableWidth / Math.max(1, word.length * 0.62));
  return Phaser.Math.Clamp(fitted, 16, maximum);
}

export class FallingEvent extends Phaser.GameObjects.Container {
  private readonly content: Phaser.GameObjects.Container;
  private readonly guideText: Phaser.GameObjects.Text;
  private readonly leftMarker: Phaser.GameObjects.Text;
  private readonly rightMarker: Phaser.GameObjects.Text;
  private readonly iconText: Phaser.GameObjects.Text;
  private readonly wordText: Phaser.GameObjects.Text;
  private readonly typedText: Phaser.GameObjects.Text;
  private readonly scoreText: Phaser.GameObjects.Text;
  private readonly word: string;
  private selected = false;

  constructor(
    scene: Phaser.Scene,
    public readonly instanceId: string,
    word: string,
    type: EventType,
    basePoints: number,
  ) {
    super(scene, 0, 0);
    this.word = word;
    const { accent, icon } = COLORS[type];
    const fontSize = responsiveFontSize(scene.scale.width, word);
    const guideFontSize = Phaser.Math.Clamp(fontSize - 10, 10, 13);

    this.guideText = scene.add
      .text(0, -fontSize - 5, word, {
        ...TEXT_STYLE,
        color: accent,
        fontSize: `${String(guideFontSize)}px`,
      })
      .setAlpha(0.78)
      .setOrigin(0.5);
    this.iconText = scene.add
      .text(0, 0, icon, {
        ...TEXT_STYLE,
        color: accent,
        fontSize: `${String(fontSize)}px`,
      })
      .setOrigin(0.5);
    this.wordText = scene.add
      .text(0, 0, word, {
        ...TEXT_STYLE,
        color: "#454a6c",
        fontSize: `${String(fontSize)}px`,
      })
      .setOrigin(0, 0.5);
    this.typedText = scene.add
      .text(0, 0, "", {
        ...TEXT_STYLE,
        color: "#0855c4",
        fontSize: `${String(fontSize)}px`,
      })
      .setOrigin(0, 0.5);
    this.scoreText = scene.add
      .text(0, fontSize + 7, `${String(basePoints)} PTS`, {
        ...TEXT_STYLE,
        color: "#454a6c",
        fontSize: "11px",
      })
      .setAlpha(0.84)
      .setOrigin(0.5);

    const gap = Math.max(7, Math.round(fontSize * 0.36));
    const mainWidth = this.iconText.width + gap + this.wordText.width;
    const mainLeft = -mainWidth / 2;
    this.iconText.x = mainLeft + this.iconText.width / 2;
    this.wordText.x = mainLeft + this.iconText.width + gap;
    this.typedText.x = this.wordText.x;

    this.leftMarker = scene.add
      .text(mainLeft - 17, 0, "‹", {
        ...TEXT_STYLE,
        color: "#00adee",
        fontSize: `${String(fontSize + 5)}px`,
      })
      .setAlpha(0)
      .setOrigin(0.5);
    this.rightMarker = scene.add
      .text(mainLeft + mainWidth + 17, 0, "›", {
        ...TEXT_STYLE,
        color: "#00adee",
        fontSize: `${String(fontSize + 5)}px`,
      })
      .setAlpha(0)
      .setOrigin(0.5);

    this.content = scene.add.container(0, 0, [
      this.guideText,
      this.leftMarker,
      this.rightMarker,
      this.iconText,
      this.wordText,
      this.typedText,
      this.scoreText,
    ]);
    this.add(this.content);
    this.setSize(
      Math.max(mainWidth + 54, this.guideText.width, this.scoreText.width) + 16,
      fontSize * 2 + 34,
    );
    scene.add.existing(this);
  }

  setTypedProgress(progress: number): void {
    const safeProgress = Phaser.Math.Clamp(progress, 0, 1);
    const typedCharacters = Math.round(this.word.length * safeProgress);
    this.typedText.setText(this.word.slice(0, typedCharacters));
  }

  setSelected(selected: boolean): void {
    this.selected = selected;
    this.scene.tweens.killTweensOf(this.content);
    this.leftMarker.setAlpha(selected ? 1 : 0);
    this.rightMarker.setAlpha(selected ? 1 : 0);
    this.guideText.setAlpha(selected ? 1 : 0.78);
    this.scoreText.setAlpha(selected ? 1 : 0.84);
    this.content.setScale(selected ? 1.05 : 1);
  }

  setResponsiveTrajectory(
    anchor: number,
    offset: number,
    progress: number,
    reducedMotion: boolean,
  ): void {
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;
    const t = Phaser.Math.Clamp(progress, 0, 1);
    const perspectiveScale = reducedMotion ? 1 : Phaser.Math.Linear(0.89, 1, t);
    const half = (this.width * perspectiveScale) / 2 + 8;

    let normalizedX = anchor;
    let normalizedY = Phaser.Math.Linear(0.22, 0.84, t);
    if (!reducedMotion) {
      const inverse = 1 - t;
      const startX = anchor + offset;
      const controlX = (startX + anchor) / 2;
      normalizedX =
        inverse * inverse * startX +
        2 * inverse * t * controlX +
        t * t * anchor;
      normalizedY =
        inverse * inverse * 0.22 + 2 * inverse * t * -0.25 + t * t * 0.84;
    }

    const x = Phaser.Math.Clamp(width * normalizedX, half, width - half);
    this.setPosition(x, height * normalizedY);
    this.setScale(perspectiveScale);
  }

  pop(): void {
    const restingScale = this.selected ? 1.05 : 1;
    this.scene.tweens.killTweensOf(this.content);
    this.content.setScale(restingScale);
    this.scene.tweens.add({
      targets: this.content,
      scale: restingScale + 0.05,
      duration: 60,
      yoyo: true,
      onComplete: () => this.content.setScale(restingScale),
    });
  }
}
